/**
 * NotificationsDrawer — выдвижная панель уведомлений (слева от сайдбара).
 *
 * Данные: TanStack Query (useNotifications + infinite scroll, useNotificationsCount).
 * Mock-режим: jotai-атомы через хуки.
 * Описания показаны обычным текстом; переходы — отдельными кнопками.
 */
import {useCallback, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAtom, useSetAtom} from 'jotai';
import {
  Bell,
  BellOff,
  Settings,
  Check,
  Phone,
  Users,
  ExternalLink,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  notificationsOpenAtom,
  notificationSettingsOpenAtom,
  dashCallIdAtom,
  dashCallOpenAtom,
  meetingDetailIdAtom,
  meetingDetailOpenAtom,
} from '@lib/atoms';
import {
  useNotifications,
  useNotificationsCount,
  useMarkRead,
  useMarkAllRead,
  useDismissNotification,
  type NotificationDisplay,
} from '@api/notifications';
import {useEnsureCommunication} from '@api/meerec/hooks';
import {cn, formatCallNumber, formatRelativeTime, MOCK_TODAY} from '@lib/utils';
import {IS_MOCK} from '@api/mock-mode';
import {toast} from 'sonner';
import {Button} from '@ui/shadcn/button';
import {EmptyState} from '@ui/primitives/EmptyState';
import {Card} from '@ui/shadcn/card';
import {Badge} from '@ui/shadcn/badge';
import {ModalPresence} from '@ui/local/ModalPresence';
import {TASK_PRIORITY_LABELS} from '@entities/task/model/types';

// ─── Общие цветовые акценты типов уведомлений и приоритетов ────────────────

type VisualKind =
  | 'priority-1'
  | 'priority-2'
  | 'digest'
  | 'analytics-report'
  | 'task';

const resolveVisualKind = (
  kind: string,
  priority: NotificationDisplay['priority'],
): VisualKind => {
  if (kind === 'instant' && priority === 1) return 'priority-1';
  if (kind === 'instant' && priority === 2) return 'priority-2';
  if (kind === 'analytics-report' || kind === 'task') return kind;
  return 'digest';
};

const KIND_META: Record<
  VisualKind,
  {label: string; chip: string; stripe: string; softBg: string}
> = {
  'priority-1': {
    label: 'Горячий лид',
    chip: 'bg-[var(--score-bad-soft)] text-[var(--score-bad)]',
    stripe: 'bg-[var(--score-bad)]',
    softBg: 'bg-[var(--score-bad-soft)]',
  },
  'priority-2': {
    label: 'Тёплый лид',
    chip: 'bg-[var(--score-warn-soft)] text-[var(--score-warn)]',
    stripe: 'bg-[var(--score-warn)]',
    softBg: 'bg-[var(--score-warn-soft)]',
  },
  'analytics-report': {
    label: 'Недельный отчёт',
    chip: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',
    stripe: 'bg-[var(--color-primary)]',
    softBg: 'bg-[var(--color-primary-soft)]',
  },
  task: {
    label: 'Задача',
    chip: 'bg-[var(--score-good-soft)] text-[var(--score-good)]',
    stripe: 'bg-[var(--score-good)]',
    softBg: 'bg-[var(--score-good-soft)]',
  },
  digest: {
    label: 'Дайджест',
    chip: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',
    stripe: 'bg-[var(--color-primary)]/70',
    softBg: 'bg-[var(--color-primary-soft)]',
  },
};

// ─── NotificationCard ───────────────────────────────────────────────────────

interface NotificationCardProps {
  item: NotificationDisplay;
  onMarkRead: () => void;
  onOpenCall: () => void;
  onOpenSource: () => void;
}

/** Референсная дата для relative-time в mock-режиме. */
const MOCK_NOW = new Date(`${MOCK_TODAY}T12:00:00Z`);

/** Убирает markdown-ссылку «Открыть звонок/встречу» из описания (кнопка — отдельно). */
const COMM_LINK_RE =
  /\n*\[Открыть (?:звонок|встречу)\]\(\/dashboard\?(?:id|meetingId)=[a-f0-9-]+\)\s*/g;
const stripCommLink = (md: string): string =>
  md.replace(COMM_LINK_RE, '').trim();

const NotificationCard = ({
  item,
  onMarkRead,
  onOpenCall,
  onOpenSource,
}: NotificationCardProps) => {
  const dismiss = useDismissNotification();
  const dismissing = useRef(false);
  const vKind = resolveVisualKind(item.kind, item.priority);
  const meta = KIND_META[vKind];
  const isReport = vKind === 'analytics-report';
  const taskPriority =
    item.kind === 'task' &&
    (item.priority === 'high' ||
      item.priority === 'medium' ||
      item.priority === 'low')
      ? item.priority
      : null;
  const tone =
    taskPriority === 'high'
      ? KIND_META['priority-1']
      : taskPriority === 'medium'
        ? KIND_META['priority-2']
        : meta;
  const chipClassName = cn(
    'text-[10px] px-1.5 py-0.5',
    tone.chip,
    item.kind === 'task' &&
      item.unread &&
      'border-current/30 bg-[var(--color-surface)]/70',
  );

  return (
    <Card
      onClick={() => {
        if (item.unread) onMarkRead();
      }}
      className={cn(
        'relative w-full text-left border border-[var(--color-border)] rounded-lg p-3 pl-3.5 gap-0',
        'hover:border-[var(--color-primary)] hover:shadow-sm transition-all',
        item.unread ? tone.softBg : 'bg-[var(--color-surface)]',
      )}
    >
      {/* Stripe */}
      <span
        className={cn(
          'absolute left-0 top-2 bottom-2 w-1 rounded-r',
          tone.stripe,
        )}
      />

      <Button
        type='button'
        variant='ghost'
        aria-label={`Прочитать уведомление: ${item.title}`}
        className='h-auto w-full flex-col items-stretch gap-0 rounded-sm p-0 text-left whitespace-normal font-normal hover:bg-transparent'
      >
        {/* Top row */}
        <div className='flex flex-wrap items-center gap-2 mb-1.5 pr-3'>
          <Badge className={chipClassName}>{meta.label}</Badge>
          {taskPriority && (
            <Badge className={chipClassName}>
              Приоритет: {TASK_PRIORITY_LABELS[taskPriority]}
            </Badge>
          )}
          <span className='text-[10px] text-[var(--color-muted-foreground)] ml-auto'>
            {formatRelativeTime(
              item.occurredAt,
              IS_MOCK ? MOCK_NOW : undefined,
            )}
          </span>
        </div>

        <p
          className={cn(
            'text-xs font-semibold text-[var(--color-foreground)] leading-snug mb-1',
            isReport && 'mb-2.5',
          )}
        >
          {item.title}
        </p>

        {/* Markdown-описание (без ссылки «Открыть звонок» — кнопка звонка отдельно). */}
        {isReport && item.reportCounts ? (
          <span className='grid grid-cols-2 gap-2'>
            {(
              [
                {
                  key: 'negative',
                  label: 'С негативной динамикой',
                  Icon: TrendingDown,
                  tone: 'text-[var(--score-warn)]',
                },
                {
                  key: 'positive',
                  label: 'С позитивной динамикой',
                  Icon: TrendingUp,
                  tone: 'text-[var(--score-good)]',
                },
              ] as const
            ).map(({key, label, Icon, tone}) => (
              <span
                key={key}
                className='flex min-w-0 flex-col gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]/70 px-3 py-2.5'
              >
                <span className='flex items-center justify-between gap-2'>
                  <span className='text-xl font-semibold leading-6 tabular-nums text-[var(--color-foreground)]'>
                    {item.reportCounts?.[key]}
                  </span>
                  <Icon
                    className={cn(
                      'size-3.5',
                      item.reportCounts?.[key]
                        ? tone
                        : 'text-[var(--color-muted-foreground)]',
                    )}
                  />
                </span>
                <span className='text-[11px] leading-4 text-[var(--color-muted-foreground)]'>
                  {label}
                </span>
              </span>
            ))}
          </span>
        ) : (
          <div className='text-[11px] text-[var(--color-muted-foreground)] leading-relaxed line-clamp-3 [&_a]:text-[var(--color-primary)] [&_a]:underline [&_strong]:font-semibold'>
            <p className='whitespace-pre-line'>
              {stripCommLink(item.description)
                .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
                .replace(/\*\*/g, '')}
            </p>
          </div>
        )}
      </Button>
      <div className='mt-2 flex flex-wrap items-center justify-between gap-2'>
        <Button
          type='button'
          variant='outline'
          size='xs'
          disabled={dismiss.isPending}
          aria-label={`Скрыть уведомление: ${item.title}`}
          title='Скрыть только уведомление — задача или звонок останутся без изменений'
          className='border-[var(--color-border)] bg-[var(--color-surface)]/70 text-[var(--color-muted-foreground)] shadow-none hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]'
          onClick={(event) => {
            event.stopPropagation();
            if (dismissing.current) return;
            dismissing.current = true;
            dismiss.mutate(item.id, {
              onError: () =>
                toast.error(
                  'Не удалось скрыть уведомление. Попробуйте ещё раз.',
                ),
              onSettled: () => {
                dismissing.current = false;
              },
            });
          }}
        >
          {dismiss.isPending ? 'Скрываем…' : 'Скрыть'}
        </Button>
        {!item.callId && item.sourcePath && (
          <Button
            variant='outline'
            size='xs'
            onClick={(event) => {
              event.stopPropagation();
              onOpenSource();
            }}
            className='ml-auto border-[var(--color-primary)]/25 bg-[var(--color-primary-soft)]/50 text-[var(--color-primary)] shadow-none hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary-soft)]'
          >
            <ExternalLink size={11} />{' '}
            {item.taskId ? 'Открыть задачу' : 'Открыть отчёт'}
          </Button>
        )}
        {item.callId && (
          <div className='ml-auto flex items-center justify-end'>
            <Button
              type='button'
              variant='ghost'
              onClick={(e) => {
                e.stopPropagation();
                onOpenCall();
              }}
              className='h-auto gap-1 text-[10px] text-[var(--color-primary)] bg-[var(--color-primary-soft)] px-1.5 py-0.5 rounded-full shrink-0 hover:bg-[var(--color-primary)] hover:text-white'
            >
              {item.isMeeting ? <Users size={9} /> : <Phone size={9} />}
              {item.isMeeting ? 'Встреча' : 'Звонок'} #
              {formatCallNumber(item.callId)}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

// ─── Main drawer ────────────────────────────────────────────────────────────

/** Drawer уведомлений — выезжает слева. */
export const NotificationsDrawer = ({motionKey}: {motionKey?: string}) => {
  const [open, setOpen] = useAtom(notificationsOpenAtom);
  const setSettingsOpen = useSetAtom(notificationSettingsOpenAtom);
  const setCallId = useSetAtom(dashCallIdAtom);
  const setCallOpen = useSetAtom(dashCallOpenAtom);
  const setMeetingId = useSetAtom(meetingDetailIdAtom);
  const setMeetingOpen = useSetAtom(meetingDetailOpenAtom);
  const navigate = useNavigate();
  const ensureCommunication = useEnsureCommunication();

  // TanStack Query hooks.
  // Лента держится всегда включённой (не только при open) — вместе с count-poll
  // она остаётся «тёплой» в кэше, и при открытии drawer уведомления видны сразу.
  const {
    data: items = [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    isPending,
    refetch,
  } = useNotifications({limit: 20});
  const {data: unreadCount, isError: countError} = useNotificationsCount();
  const markReadMut = useMarkRead();
  const markAllReadMut = useMarkAllRead();

  // Infinite scroll: IntersectionObserver на sentinel.
  // `open` в зависимостях обязателен: сентинель рендерится только при открытом
  // дровере, а лента (хук) держится всегда включённой. Без `open` observer не
  // пересоздаётся при открытии панели — скролл не подгружает следующие страницы.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) void fetchNextPage();
      },
      {rootMargin: '200px'},
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleMarkRead = useCallback(
    (item: NotificationDisplay) => {
      if (item.unread && item.id) markReadMut.mutate(item.id);
    },
    [markReadMut],
  );

  const handleOpenCall = useCallback(
    async (item: NotificationDisplay) => {
      handleMarkRead(item);
      if (!item.callId) return;

      // Пока уведомление ждало открытия, звонок мог стать недоступен (404/403).
      // Проверяем до открытия — иначе пользователь получает пустую карточку с
      // ошибкой. Уже отмеченный решённым звонок открывается как обычно:
      // карточка сама покажет отметку вместо кнопки.
      const check = await ensureCommunication(item.callId);
      if (!check.ok) {
        toast.info(
          check.reason === 'gone'
            ? item.isMeeting
              ? 'Встреча недоступна'
              : 'Звонок недоступен'
            : item.isMeeting
              ? 'Не удалось открыть встречу, попробуйте позже'
              : 'Не удалось открыть звонок, попробуйте позже',
          {id: `call-unavailable-${item.callId}`},
        );
        return;
      }

      // Уведомления — из очереди внимания (p1/p2). Открываем на дашборде, чтобы
      // isDashboardContext=true и кнопка «Отметить решённым» была доступна.
      // Встреча — в своей карточке; URL-синк запишет ?meetingId= / ?id= сам.
      setOpen(false);
      navigate('/dashboard');
      if (item.isMeeting) {
        setMeetingId(item.callId);
        setMeetingOpen(true);
        return;
      }
      setCallId(item.callId);
      setCallOpen(true);
    },
    [
      handleMarkRead,
      ensureCommunication,
      setOpen,
      navigate,
      setMeetingId,
      setMeetingOpen,
      setCallId,
      setCallOpen,
    ],
  );

  const renderPanel = () => {
    if (!open) return null;

    return (
      <>
        {/* Overlay — не затемняет левое меню. */}
        <div
          className='modal-overlay fixed top-0 right-0 bottom-0 bg-black/15'
          style={{
            left: 'var(--sidebar-w)',
            zIndex: 'calc(var(--z-notifications) - 5)',
          }}
          onClick={() => setOpen(false)}
        />

        {/* Panel */}
        <div
          className='modal-panel-left fixed top-0 bottom-0 w-[400px] max-w-[90vw] bg-[var(--color-surface)] border-r border-[var(--color-border)] flex flex-col shadow-2xl'
          style={{left: 'var(--sidebar-w)', zIndex: 'var(--z-notifications)'}}
        >
          {/* Header */}
          <div className='flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)] shrink-0'>
            <div className='flex items-center gap-2'>
              <Bell size={16} className='text-[var(--color-foreground)]' />
              <span className='text-sm font-semibold'>Уведомления</span>
              {!countError && unreadCount !== undefined && unreadCount > 0 && (
                <span className='inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[var(--score-bad)] text-white text-[10px] font-bold'>
                  {unreadCount}
                </span>
              )}
            </div>
            <div className='flex items-center gap-1'>
              <Button
                type='button'
                variant='link'
                size='xs'
                onClick={() =>
                  markAllReadMut.mutate(undefined, {
                    onError: () =>
                      toast.error(
                        'Не все уведомления удалось прочитать. Повторите действие.',
                      ),
                  })
                }
                disabled={unreadCount === 0 || markAllReadMut.isPending}
                className='gap-1 px-1 text-[11px] text-[var(--color-primary)] no-underline hover:underline disabled:no-underline'
              >
                <Check size={11} />
                Прочитать все
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='icon-xs'
                onClick={() => setSettingsOpen(true)}
                className='text-[var(--color-secondary)] hover:bg-[var(--color-muted)]'
                title='Настройки уведомлений'
              >
                <Settings size={14} />
              </Button>
            </div>
          </div>

          {/* Body */}
          <div className='flex-1 overflow-y-auto p-3 space-y-2'>
            {countError && (
              <p
                role='status'
                className='text-xs text-[var(--color-muted-foreground)]'
              >
                Счётчик временно недоступен.
              </p>
            )}
            {isError ? (
              <div role='alert' className='space-y-2 text-sm'>
                <p>Не удалось загрузить уведомления.</p>
                <Button variant='outline' onClick={() => void refetch()}>
                  Повторить
                </Button>
              </div>
            ) : isPending ? (
              <p role='status'>Загрузка…</p>
            ) : items.length === 0 ? (
              <EmptyState
                icon={<BellOff size={26} />}
                title='Уведомлений пока нет'
                className='py-10'
              />
            ) : (
              <>
                {items.map((item) => (
                  <NotificationCard
                    key={item.id}
                    item={item}
                    onMarkRead={() => handleMarkRead(item)}
                    onOpenCall={() => void handleOpenCall(item)}
                    onOpenSource={() => {
                      if (!item.sourcePath) return;
                      handleMarkRead(item);
                      setOpen(false);
                      navigate(item.sourcePath);
                    }}
                  />
                ))}
                {/* Sentinel для infinite scroll */}
                {hasNextPage && <div ref={sentinelRef} className='h-4' />}
                {isFetchingNextPage && (
                  <p className='text-center text-[11px] text-[var(--color-muted-foreground)] py-2'>
                    Загрузка…
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </>
    );
  };
  return <ModalPresence key={motionKey}>{renderPanel()}</ModalPresence>;
};
